import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import { useClerk, useAuth as useClerkAuth, useUser } from "@clerk/clerk-react";
import PropTypes from "prop-types";
import axios from "api/axiosInstance";
import AuthApi from "api/auth";
import BucketApi from "api/bucketApi";
import DotSpinner from "common/Loader/Loader";

// Activar logs de depuración en desarrollo
const debugLog = (area, action, data = {}) => {
  // Siempre loggear en desarrollo para ayudar a depurar
  if (process.env.NODE_ENV === "development") {
    console.log(`[Auth Debug][${area}] ${action}`, {
      timestamp: new Date().toISOString(),
      ...data,
    });
  }
};

const AuthContext = createContext(null);

// Constantes para intervalos de tiempo
const TOKEN_CHECK_INTERVAL = 4 * 60 * 1000; // 4 minutos
const SESSION_VERIFY_INTERVAL = 15 * 60 * 1000; // 15 minutos
const VISIBLE_CHECK_DELAY = 3000; // 3 segundos después de que la página se vuelve visible

export const AuthProvider = ({ children }) => {
  const { session, signOut } = useClerk();
  const clerkAuth = useClerkAuth();
  const clerkUser = useUser();
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
  const [sessionError, setSessionError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Referencias para controlar los timers y estados
  const lastVerificationRef = useRef(Date.now());
  const tokenRefreshTimeoutRef = useRef(null);
  const sessionCheckIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const isLoadingUserDataRef = useRef(false);
  const initializeStartedRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const sessionStoredRef = useRef(false);

  // Referencias adicionales para debugging
  const dataLoadedRef = useRef(false);
  const loadingSetFalseRef = useRef(false);

  // Función para limpiar el estado de autenticación
  const clearAuthState = useCallback(() => {
    localStorage.removeItem("user");
    setUser(null);
    setPermissions([]);
    setProfileImage(null);
    sessionStoredRef.current = false;
    dataLoadedRef.current = false;

    // Una vez que limpiamos todo, ya no estamos cargando
    setLoading(false);
    loadingSetFalseRef.current = true;

    // Limpiar todos los timers
    if (tokenRefreshTimeoutRef.current) {
      clearTimeout(tokenRefreshTimeoutRef.current);
      tokenRefreshTimeoutRef.current = null;
    }
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
      sessionCheckIntervalRef.current = null;
    }
  }, []);

  // Función de logout
  const logout = useCallback(async () => {
    debugLog("Logout", "Iniciando proceso de logout");
    try {
      // Primero limpiar estado local
      clearAuthState();

      // Luego cerrar sesión en backend
      try {
        await axios.post("/auth/logout", {}, { skipAuth: true });
      } catch (error) {
        console.warn("Error al cerrar sesión en backend:", error);
      }

      // Finalmente cerrar sesión en Clerk
      if (session) {
        await signOut();
      }

      debugLog("Logout", "Proceso de logout completado");
    } catch (error) {
      debugLog("Logout", "Error durante logout", { error });
      clearAuthState(); // Asegurar limpieza incluso si hay error
    }
  }, [session, clearAuthState, signOut]);

  // Función para cargar datos del usuario autenticado
  const loadUserData = useCallback(
    async (retryOnFailure = true) => {
      // Evitar cargas múltiples simultáneas
      if (isLoadingUserDataRef.current) {
        debugLog("LoadUserData", "Ya hay una carga en proceso, saltando");
        return false;
      }

      isLoadingUserDataRef.current = true;
      debugLog("LoadUserData", "Cargando datos del usuario");

      try {
        // Obtener token fresco
        if (!axios.defaults.headers.common["Authorization"] && session) {
          const token = await axios.refreshToken();
          if (!token) {
            throw new Error("No se pudo obtener token de autenticación");
          }
        }

        // Realizar solicitud para obtener datos del usuario
        const response = await AuthApi.GetCurrentUser();

        if (!response.data.success) {
          throw new Error("Error obteniendo datos del usuario");
        }

        const email = clerkUser.user?.primaryEmailAddress?.emailAddress;

        if (!email) {
          throw new Error("No se pudo obtener el email del usuario desde Clerk");
        }

        const userData = {
          ...response.data.user,
          _id: response.data.user.id,
          token: axios.defaults.headers.common["Authorization"]?.split(" ")[1],
          email: email,
          roles: response.data.user.roles || [],
          permissions: response.data.user.permissions || [],
        };

        // Guardar en localStorage y estado
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        lastVerificationRef.current = Date.now();
        sessionStoredRef.current = true;
        dataLoadedRef.current = true; // Marcar que los datos se cargaron con éxito

        // Terminar la carga ahora que tenemos los datos
        setLoading(false);
        loadingSetFalseRef.current = true;
        debugLog("LoadUserData", "Loading set to false");

        // Cargar permisos e imagen de perfil en paralelo sin bloquear
        Promise.allSettled([
          BucketApi.getProfileImageUrl().then((imageResponse) => {
            setProfileImage(imageResponse.data?.file_url || null);
          }),

          axios.get("/groups/").then((groupsResponse) => {
            if (userData.roles?.length > 0) {
              const userGroups = groupsResponse.data.filter((group) =>
                userData.roles.includes(group.name)
              );

              Promise.allSettled(
                userGroups.map((group) => axios.get(`/permissions/${group.id}/group_permissions/`))
              ).then((permissionsResponses) => {
                const allPermissions = permissionsResponses
                  .filter((response) => response.status === "fulfilled")
                  .flatMap((response) => response.value.data);

                setPermissions(allPermissions);
              });
            }
          }),
        ]).catch((error) => {
          console.warn("Error cargando datos adicionales:", error);
        });

        debugLog("LoadUserData", "Datos del usuario cargados exitosamente", { userData });
        setSessionError(null);
        return true;
      } catch (error) {
        debugLog("LoadUserData", "Error cargando datos del usuario", { error });

        // Si es un error de autenticación y tenemos opción de reintentar
        if (error.response?.status === 401 && retryOnFailure && retryCount < 3) {
          debugLog("LoadUserData", "Reintentando con token fresco");
          setRetryCount((prevCount) => prevCount + 1);

          try {
            await axios.refreshToken();
            return loadUserData(false);
          } catch (refreshError) {
            debugLog("LoadUserData", "Error en reintento de carga", { refreshError });
          }
        }

        setSessionError(error.message || "Error de autenticación");

        // Asegurarnos de terminar el loading incluso si hay error
        setLoading(false);
        loadingSetFalseRef.current = true;
        debugLog("LoadUserData", "Loading set to false (error)");

        return false;
      } finally {
        isLoadingUserDataRef.current = false;
      }
    },
    [session, clerkUser.user, retryCount]
  );

  // Verificar si el usuario está realmente autenticado
  const verifyAuthentication = useCallback(async () => {
    try {
      if (!session || !clerkUser.user) {
        debugLog("VerifyAuth", "No hay sesión de Clerk");
        return false;
      }

      // Verificar si tenemos usuario guardado en localStorage
      const storedUser = localStorage.getItem("user");
      if (!storedUser) {
        debugLog("VerifyAuth", "No hay usuario en localStorage");
        return false;
      }

      // Si aún no tenemos token en los headers, intentar cargarlo
      if (!axios.defaults.headers.common["Authorization"]) {
        // Intentar cargar token desde localStorage
        const userData = JSON.parse(storedUser);
        if (userData?.token) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${userData.token}`;
        } else {
          // Si no hay token en localStorage, obtener uno nuevo
          const token = await axios.refreshToken();
          if (!token) {
            debugLog("VerifyAuth", "No se pudo obtener token");
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      debugLog("VerifyAuth", "Error verificando autenticación", { error });
      return false;
    }
  }, [session, clerkUser.user]);

  // Manejar el evento de sesión expirada
  useEffect(() => {
    const handleSessionExpired = (event) => {
      debugLog("SessionEvent", "Evento de sesión expirada recibido", { event });

      // Solo intentar renovar si estamos autenticados y no estamos ya refrescando
      if (clerkAuth.isSignedIn && session && !isRefreshingRef.current) {
        // Intentamos refrescar silenciosamente
        axios
          .refreshToken()
          .then((token) => {
            if (token) {
              debugLog("SessionEvent", "Sesión renovada exitosamente");
            } else {
              setSessionError("Tu sesión necesita ser renovada. Por favor, refresca la página.");
            }
          })
          .catch((error) => {
            debugLog("SessionEvent", "No se pudo renovar la sesión", { error });
            setSessionError("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
          });
      }
    };

    window.addEventListener("auth:sessionExpired", handleSessionExpired);

    return () => {
      window.removeEventListener("auth:sessionExpired", handleSessionExpired);
    };
  }, [clerkAuth.isSignedIn, session]);

  // Efecto para manejar cambios de visibilidad de la página
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      debugLog("Visibility", `Cambio de visibilidad: ${isVisible ? "visible" : "oculto"}`);
      setIsPageVisible(isVisible);

      if (isVisible && user && !isRefreshingRef.current) {
        // Usar setTimeout para evitar múltiples actualizaciones
        setTimeout(() => {
          const timeSinceLastVerification = Date.now() - lastVerificationRef.current;

          if (timeSinceLastVerification > SESSION_VERIFY_INTERVAL) {
            debugLog("Visibility", "Verificando sesión después de inactividad");

            // Verificar si el token está por expirar
            const isTokenNearExpiry = axios.isTokenExpiringSoon();
            if (isTokenNearExpiry) {
              isRefreshingRef.current = true;
              axios
                .refreshToken()
                .then(() => loadUserData(false))
                .catch((error) => {
                  debugLog("Visibility", "Error renovando token después de inactividad", { error });
                  setSessionError(
                    "Tu sesión necesita ser renovada. Por favor, refresca la página."
                  );
                })
                .finally(() => {
                  isRefreshingRef.current = false;
                });
            }
          }
        }, VISIBLE_CHECK_DELAY);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user, loadUserData]);

  // Efecto para monitorear cambios en la sesión de Clerk
  useEffect(() => {
    // Sólo ejecutar si Clerk está cargado
    if (!clerkAuth.isLoaded) return;

    debugLog("ClerkSession", "Estado de Clerk actualizado", {
      isSignedIn: clerkAuth.isSignedIn,
      hasSession: !!session,
      hasUser: !!user,
      sessionStored: sessionStoredRef.current,
      loadingState: loading,
      dataLoaded: dataLoadedRef.current,
      loadingSetFalse: loadingSetFalseRef.current,
    });

    // Si hay sesión en Clerk pero no tenemos usuario, cargar datos
    if (
      clerkAuth.isSignedIn &&
      session &&
      !user &&
      !isLoadingUserDataRef.current &&
      !isRefreshingRef.current
    ) {
      debugLog("ClerkSession", "Sesión de Clerk activa pero sin usuario local, cargando datos");

      verifyAuthentication().then((isAuthenticated) => {
        if (isAuthenticated) {
          isRefreshingRef.current = true;
          loadUserData().finally(() => {
            isRefreshingRef.current = false;
          });
        } else {
          // Forzar establecer loading a false si falló la verificación
          setLoading(false);
          loadingSetFalseRef.current = true;
          debugLog("ClerkSession", "Loading set to false (verificación fallida)");
        }
      });
    }

    // Si no hay sesión en Clerk pero tenemos usuario, limpiarlo
    if (!clerkAuth.isSignedIn && user) {
      debugLog("ClerkSession", "No hay sesión de Clerk pero existe usuario local, limpiando");
      clearAuthState();
    }

    // Si Clerk está cargado pero no inició sesión, no deberíamos estar cargando
    if (clerkAuth.isLoaded && !clerkAuth.isSignedIn && loading) {
      setLoading(false);
      loadingSetFalseRef.current = true;
      debugLog("ClerkSession", "Loading set to false (Clerk cargado sin sesión)");
    }

    // Terminar la carga si Clerk está cargado, hay sesión, hay usuario, pero seguimos en estado de carga
    if (clerkAuth.isLoaded && clerkAuth.isSignedIn && user && loading && dataLoadedRef.current) {
      setLoading(false);
      loadingSetFalseRef.current = true;
      debugLog("ClerkSession", "Loading set to false (todo cargado)");
    }
  }, [
    clerkAuth.isLoaded,
    clerkAuth.isSignedIn,
    session,
    user,
    clearAuthState,
    loadUserData,
    verifyAuthentication,
    loading,
  ]);

  // Efecto principal para inicialización del contexto
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // Prevenir inicialización múltiple
      if (initializeStartedRef.current || hasInitializedRef.current) {
        return;
      }

      // Esperar a que Clerk esté cargado
      if (!clerkAuth.isLoaded) {
        return;
      }

      // Marcar como iniciado para evitar duplicación
      initializeStartedRef.current = true;

      debugLog("Init", "Inicializando autenticación", {
        isSignedIn: clerkAuth.isSignedIn,
        hasSession: !!session,
        isPageVisible,
      });

      // Si no hay sesión de Clerk
      if (!clerkAuth.isSignedIn || !session) {
        clearAuthState();
        if (mounted) {
          setLoading(false);
          loadingSetFalseRef.current = true;
          debugLog("Init", "Loading set to false (sin sesión de Clerk)");
          hasInitializedRef.current = true;
          initializeStartedRef.current = false;
        }
        return;
      }

      try {
        // Verificar si ya tenemos datos en localStorage
        const storedUser = localStorage.getItem("user");
        if (storedUser && !user) {
          // Cargar datos desde localStorage temporalmente para acelerar
          const userData = JSON.parse(storedUser);
          setUser(userData);

          // Configurar token en axios si existe
          if (userData.token) {
            axios.defaults.headers.common.Authorization = `Bearer ${userData.token}`;
          }

          // Aún así, verificar autenticación y actualizar token si es necesario
          const isAuthenticated = await verifyAuthentication();
          if (!isAuthenticated) {
            // Si la verificación falla, cargar datos completos
            await loadUserData();
          } else {
            // Marcar que tenemos datos y terminar la carga
            dataLoadedRef.current = true;
            setLoading(false);
            loadingSetFalseRef.current = true;
            debugLog("Init", "Loading set to false (usuario en localStorage)");
          }
        } else {
          // Cargar datos del usuario desde el servidor
          await loadUserData();
        }
      } catch (error) {
        debugLog("Init", "Error en inicialización", { error });
        if (mounted) {
          clearAuthState();
        }
      } finally {
        if (mounted) {
          // Asegurar que loading se estableció en false
          if (loading) {
            setLoading(false);
            loadingSetFalseRef.current = true;
            debugLog("Init", "Loading set to false (finally)");
          }

          hasInitializedRef.current = true;
          initializeStartedRef.current = false;
        }
      }
    };

    // Configurar verificación periódica de sesión
    const setupPeriodicSessionCheck = () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }

      if (user && session) {
        debugLog("Session", "Configurando verificación periódica");
        sessionCheckIntervalRef.current = setInterval(() => {
          if (isPageVisible && !isRefreshingRef.current) {
            debugLog("Session", "Verificación periódica de sesión");

            // Verificar si el token está por expirar
            const isTokenNearExpiry = axios.isTokenExpiringSoon();
            if (isTokenNearExpiry) {
              isRefreshingRef.current = true;
              axios
                .refreshToken()
                .catch((error) => {
                  debugLog("Session", "Error en renovación periódica", { error });
                })
                .finally(() => {
                  isRefreshingRef.current = false;
                });
            }
          }
        }, TOKEN_CHECK_INTERVAL);
      }
    };

    // Intentar inicializar inmediatamente
    initializeAuth();

    // Configurar un timeout de seguridad para evitar quedarse en loading para siempre
    const safetyTimeout = setTimeout(() => {
      if (loading && mounted) {
        debugLog("Init", "Safety timeout ejecutado para evitar loading infinito");
        setLoading(false);
        loadingSetFalseRef.current = true;
      }
    }, 5000); // 5 segundos como máximo para inicializar

    // Solo configurar verificación periódica si ya tenemos usuario
    if (user && session) {
      setupPeriodicSessionCheck();
    }

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
    };
  }, [
    clerkAuth.isLoaded,
    clerkAuth.isSignedIn,
    session,
    isPageVisible,
    loadUserData,
    clearAuthState,
    user,
    verifyAuthentication,
    loading,
  ]);

  // Manejador para actualizar la sesión
  const refreshSession = useCallback(async () => {
    if (isRefreshingRef.current) {
      debugLog("Refresh", "Ya hay una renovación en curso, esperando...");
      return false;
    }

    isRefreshingRef.current = true;
    debugLog("Refresh", "Actualizando sesión manualmente");

    try {
      const token = await axios.refreshToken();
      if (token) {
        await loadUserData(false);
        setSessionError(null);
        return true;
      }
      return false;
    } catch (error) {
      debugLog("Refresh", "Error actualizando sesión", { error });
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [loadUserData]);

  // Valores expuestos por el contexto
  const contextValue = {
    user,
    permissions,
    profileImage,
    loading: !clerkAuth.isLoaded || loading,
    isSignedIn: clerkAuth.isSignedIn && !!user, // ambos deben ser verdaderos
    sessionError,
    hasRole: (roleName) => user?.roles?.includes(roleName) || false,
    hasPermission: (permission) => permissions?.includes(permission) || false,
    isAdmin: () => user?.roles?.includes("ADMIN") || false,
    logout,
    refreshSession,
    dismissSessionError: () => setSessionError(null),
  };

  // Debug para el problema de carga infinita
  useEffect(() => {
    if (loading) {
      debugLog("LoadingState", "Todavía en loading", {
        clerkLoaded: clerkAuth.isLoaded,
        clerkSignedIn: clerkAuth.isSignedIn,
        hasUser: !!user,
        dataLoaded: dataLoadedRef.current,
        loadingSetFalse: loadingSetFalseRef.current,
        initializeStarted: initializeStartedRef.current,
        hasInitialized: hasInitializedRef.current,
      });
    }
  }, [loading, clerkAuth.isLoaded, clerkAuth.isSignedIn, user]);

  if (!clerkAuth.isLoaded || loading) {
    debugLog("Render", "Mostrando loader inicial");
    return <DotSpinner />;
  }

  // Render con SessionErrorBanner si hay un error de sesión
  return (
    <AuthContext.Provider value={contextValue}>
      {sessionError && clerkAuth.isSignedIn && (
        <SessionErrorBanner
          error={sessionError}
          onDismiss={() => setSessionError(null)}
          onRefresh={refreshSession}
        />
      )}
      {children}
    </AuthContext.Provider>
  );
};

// Componente para mostrar errores de sesión
const SessionErrorBanner = ({ error, onDismiss, onRefresh }) => {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: "#f8d7da",
        color: "#721c24",
        padding: "10px 20px",
        zIndex: 9999,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      <span>{error}</span>
      <div>
        <button
          onClick={onRefresh}
          style={{
            backgroundColor: "#721c24",
            color: "white",
            border: "none",
            padding: "5px 10px",
            marginRight: "10px",
            cursor: "pointer",
            borderRadius: "3px",
          }}
        >
          Renovar sesión
        </button>
        <button
          onClick={onDismiss}
          style={{
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: "16px",
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

SessionErrorBanner.propTypes = {
  error: PropTypes.string.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de un AuthProvider");
  }
  return context;
};

export default AuthContext;
